import {RawLocation} from 'vue-router';
import {Weekdays} from '@/helpers/dateHelpers/Weekdays.enum';
import {minutesToTime} from '@/helpers/dateHelpers/timeConversion.helper';
import {plural, WEEKDAY_SHORT} from '@/components/common/entity-picker/entityPicker.helper';
import {
    activeStudentCount,
    AVERAGE_MAX_CHILDREN_PER_COURSE,
    estimatedFreeSeats,
} from '@/components/common/entity-picker/roomContext.helper';
import {idOf, textOf} from '@/components/app/search/searchQuickViewData';

/** Course as loaded for the quick view (references populated where the user may read them) */
export interface ScheduleCourse {
    _id: unknown;
    customId?: string;
    dayOfWeek?: Weekdays;
    timeRange?: { startTime: number; endTime: number };
    subject?: unknown;
    teacher?: unknown;
    institution?: unknown;
    room?: unknown;
    studentItems?: Array<{ student: unknown; endOfParticipation?: Date; resumptionOfParticipation?: Date }>;
}

/** Tone classes of modern.css used to colour children / locations / teachers */
export const SCHEDULE_TONES = ['tone-info', 'tone-good', 'tone-purple', 'tone-warn', 'tone-teal', 'tone-bad'];

/** One course line of the list (per child or per weekday) */
export type ScheduleRow = {
    courseId: string;
    /** null for a course without weekday / time */
    day: Weekdays | null;
    when: string;
    /** "14:00–15:00" (used in weekday sections) */
    time: string;
    title: string;
    details: string[];
    /** "4 Kinder · noch ~2 frei (Ø 6)" – only if children were counted */
    seats?: string;
    seatsTone?: string;
    tone: string;
};

/** A group of rows: one child (parent view) or one weekday (teacher / institution) */
export type ScheduleSection = {
    label: string;
    to?: RawLocation;
    tone?: string;
    hint?: string;
    /** Rows show only the time, the weekday is the section label */
    byDay?: boolean;
    rows: ScheduleRow[];
};

/** A block of the mini timetable */
export type ScheduleBlock = {
    courseId: string;
    day: Weekdays;
    start: number;
    end: number;
    label: string;
    tooltip: string;
    tone: string;
};

export type ScheduleLegend = { label: string; tone: string };

export type QuickSchedule = {
    title: string;
    summary: string;
    sections: ScheduleSection[];
    blocks: ScheduleBlock[];
    legend: ScheduleLegend[];
};

const WEEK: Weekdays[] = [
    Weekdays.MONDAY, Weekdays.TUESDAY, Weekdays.WEDNESDAY, Weekdays.THURSDAY,
    Weekdays.FRIDAY, Weekdays.SATURDAY, Weekdays.SUNDAY,
];

export function hasSlot(course: ScheduleCourse): course is ScheduleCourse & { dayOfWeek: Weekdays; timeRange: { startTime: number; endTime: number } } {
    return typeof course.dayOfWeek === 'number' && !!course.timeRange
        && typeof course.timeRange.startTime === 'number' && typeof course.timeRange.endTime === 'number';
}

/** Sorted by weekday and start time, courses without a slot last */
export function sortCourses<T extends ScheduleCourse>(courses: T[]): T[] {
    const key = (course: ScheduleCourse) => (hasSlot(course) ? course.dayOfWeek * 1440 + course.timeRange.startTime : Number.MAX_SAFE_INTEGER);
    return [...courses].sort((a, b) => key(a) - key(b));
}

/** "Mo 14:00–15:00" */
export function whenOf(course: ScheduleCourse): string {
    if (!hasSlot(course)) {
        return 'ohne Termin';
    }
    return `${WEEKDAY_SHORT[course.dayOfWeek]} ${minutesToTime(course.timeRange.startTime)}–${minutesToTime(course.timeRange.endTime)}`;
}

/** "4 Kinder · noch ~2 frei (Ø 6)" with the same planning rule as the room picker */
export function seatsOf(course: ScheduleCourse): { text: string; tone: string } | null {
    if (!Array.isArray(course.studentItems)) {
        return null;
    }
    const count = activeStudentCount(course as any);
    const free = estimatedFreeSeats([course as any]);
    const children = plural(count, 'Kind', 'Kinder');
    if (!free) {
        return {text: `${children} · voll (Ø ${AVERAGE_MAX_CHILDREN_PER_COURSE})`, tone: 'tone-warn'};
    }
    return {text: `${children} · noch ~${free} frei (Ø ${AVERAGE_MAX_CHILDREN_PER_COURSE})`, tone: 'tone-good'};
}

const OBJECT_ID = /^[0-9a-f]{24}$/i;

/** Name of a populated reference; an unresolved id (missing or unreadable record) yields '' instead of the raw id */
export function nameOf(value: unknown): string {
    const text = textOf(value);
    return OBJECT_ID.test(text) ? '' : text;
}

function roomText(course: ScheduleCourse): string {
    const room = nameOf(course.room);
    return room ? `Raum ${room}` : '';
}

/** Detail parts of a course; parts the view already shows (e.g. the teacher in the teacher view) are left out */
export function courseDetails(course: ScheduleCourse, omit: Array<'teacher' | 'institution'> = []): string[] {
    return [
        omit.includes('teacher') ? '' : nameOf(course.teacher),
        omit.includes('institution') ? '' : nameOf(course.institution),
        roomText(course),
    ].filter((el) => el);
}

export function courseRow(course: ScheduleCourse, tone: string, omit: Array<'teacher' | 'institution'> = [], withSeats = false): ScheduleRow {
    const seats = withSeats ? seatsOf(course) : null;
    return {
        courseId: idOf(course._id) || String(course._id),
        day: hasSlot(course) ? course.dayOfWeek : null,
        when: whenOf(course),
        time: hasSlot(course) ? `${minutesToTime(course.timeRange.startTime)}–${minutesToTime(course.timeRange.endTime)}` : 'ohne Termin',
        title: textOf(course.subject) || course.customId || 'Kurs',
        details: courseDetails(course, omit),
        seats: seats ? seats.text : undefined,
        seatsTone: seats ? seats.tone : undefined,
        tone,
    };
}

/** Short label of a block, truncated to stay readable in the narrow grid */
function shortLabel(text: string, max = 14): string {
    const value = (text || '').trim();
    return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

export function courseBlock(course: ScheduleCourse, tone: string, label: string, owner = ''): ScheduleBlock | null {
    if (!hasSlot(course)) {
        return null;
    }
    const seats = seatsOf(course);
    const tooltip = [
        [whenOf(course), owner].filter((el) => el).join(' · '),
        [textOf(course.subject), course.customId].filter((el) => el).join(' · '),
        courseDetails(course).join(' · '),
        seats ? seats.text : '',
    ].filter((el) => el).join('\n');
    return {
        courseId: idOf(course._id) || String(course._id),
        day: course.dayOfWeek,
        start: course.timeRange.startTime,
        end: Math.max(course.timeRange.endTime, course.timeRange.startTime + 15),
        label: shortLabel(label || textOf(course.subject) || course.customId || 'Kurs'),
        tooltip,
        tone,
    };
}

/** Distinct weekdays of the courses as "Mo, Di, Do" */
export function weekdaysText(courses: ScheduleCourse[]): string {
    const days = Array.from(new Set(courses.filter(hasSlot).map((course) => course.dayOfWeek))).sort((a, b) => a - b);
    return days.map((day) => WEEKDAY_SHORT[day]).join(', ');
}

/** Assigns a tone per key (child, location, teacher) in order of appearance */
export function toneAssigner(): (key: string) => string {
    const tones = new Map<string, string>();
    return (key: string) => {
        if (!tones.has(key)) {
            tones.set(key, SCHEDULE_TONES[tones.size % SCHEDULE_TONES.length]);
        }
        return tones.get(key) as string;
    };
}

/** Rows grouped by weekday (teacher / institution view) */
export function sectionsByWeekday(rows: ScheduleRow[]): ScheduleSection[] {
    const sections: ScheduleSection[] = WEEK.map((day) => ({label: Weekdays.Record[day].text, byDay: true, rows: rows.filter((row) => row.day === day)}));
    sections.push({label: 'Ohne Termin', rows: rows.filter((row) => row.day === null)});
    return sections.filter((section) => section.rows.length);
}

// ---------------------------------------------------------------------------------------------
// Mini timetable layout
// ---------------------------------------------------------------------------------------------

export type GridBlock = ScheduleBlock & {
    /** Position in percent of the day column / of the grid height */
    top: number;
    height: number;
    left: number;
    width: number;
    time: string;
};

export type GridColumn = { day: Weekdays; label: string; blocks: GridBlock[] };

export type GridTick = { label: string; top: number };

export type TimetableGrid = {
    columns: GridColumn[];
    ticks: GridTick[];
    /** Height of the grid in px */
    height: number;
};

const MAX_GRID_HEIGHT = 300;
const MIN_GRID_HEIGHT = 120;
const PX_PER_HOUR = 46;

/** Side-by-side lanes for overlapping blocks of one day */
function layoutDay(blocks: ScheduleBlock[]): Array<ScheduleBlock & { lane: number; lanes: number }> {
    const sorted = [...blocks].sort((a, b) => a.start - b.start || b.end - a.end);
    const result: Array<ScheduleBlock & { lane: number; lanes: number }> = [];
    let cluster: Array<ScheduleBlock & { lane: number; lanes: number }> = [];
    let clusterEnd = -1;
    const closeCluster = () => {
        const lanes = cluster.reduce((max, block) => Math.max(max, block.lane + 1), 1);
        cluster.forEach((block) => {
            block.lanes = lanes;
        });
        result.push(...cluster);
        cluster = [];
    };
    for (const block of sorted) {
        if (cluster.length && block.start >= clusterEnd) {
            closeCluster();
        }
        const laneEnds: number[] = [];
        cluster.forEach((other) => {
            laneEnds[other.lane] = Math.max(laneEnds[other.lane] || 0, other.end);
        });
        let lane = laneEnds.findIndex((end) => end <= block.start);
        if (lane < 0) {
            lane = laneEnds.length;
        }
        cluster.push({...block, lane, lanes: 1});
        clusterEnd = Math.max(clusterEnd, block.end);
    }
    if (cluster.length) {
        closeCluster();
    }
    return result;
}

/**
 * Week grid Mo–Fr (Sa/So only with courses), time axis limited to the full hours around the courses.
 * Returns null without blocks.
 */
export function buildTimetableGrid(blocks: ScheduleBlock[]): TimetableGrid | null {
    if (!blocks.length) {
        return null;
    }
    const from = Math.floor(Math.min(...blocks.map((block) => block.start)) / 60) * 60;
    const to = Math.max(from + 60, Math.ceil(Math.max(...blocks.map((block) => block.end)) / 60) * 60);
    const range = to - from;
    const height = Math.max(MIN_GRID_HEIGHT, Math.min(MAX_GRID_HEIGHT, (range / 60) * PX_PER_HOUR));
    const step = (height / range) * 60 < 22 ? 120 : 60;
    const ticks: GridTick[] = [];
    for (let minute = from; minute < to; minute += step) {
        ticks.push({label: minutesToTime(minute), top: ((minute - from) / range) * 100});
    }
    const days = WEEK.filter((day) => day <= Weekdays.FRIDAY || blocks.some((block) => block.day === day));
    const columns = days.map((day) => ({
        day,
        label: WEEKDAY_SHORT[day],
        blocks: layoutDay(blocks.filter((block) => block.day === day)).map((block) => ({
            ...block,
            top: ((block.start - from) / range) * 100,
            height: ((block.end - block.start) / range) * 100,
            left: (block.lane / block.lanes) * 100,
            width: 100 / block.lanes,
            time: minutesToTime(block.start),
        })),
    }));
    return {columns, ticks, height};
}
